import { z } from 'zod';
import { positionCodeSchema } from './positions';
import { uuidSchema } from './uuid';

/**
 * Access on board (PLAN §2.3, §3.10). Outbox mutations of the core (PLAN §3.2): today only the
 * Master's "End access" from the phone; `sync_push` (Task 8) validates them with the same schema.
 */

export const revokeAssignmentPayloadSchema = z.object({
  assignment_id: uuidSchema,
  reason: z.string().trim().max(500).nullable(),
});

export const coreMutationSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('core.revoke_assignment'), payload: revokeAssignmentPayloadSchema }),
]);
export type CoreMutation = z.infer<typeof coreMutationSchema>;

/** One row of `my_assignments()`: an active position of the signed-in person. */
export const myAssignmentSchema = z.object({
  assignment_id: uuidSchema,
  vessel_id: uuidSchema,
  vessel_name: z.string(),
  imo: z.string(),
  company_id: uuidSchema,
  position_code: positionCodeSchema,
  starts_at: z.string(),
});
export type MyAssignment = z.infer<typeof myAssignmentSchema>;

/** Error codes of the Edge Functions (`supabase/functions/_shared/http.ts`). */
export const FUNCTION_ERRORS = [
  'method_not_allowed',
  'invalid_request',
  'not_signed_in',
  'not_allowed',
  'not_found',
  'position_taken',
  'admin_account',
  'server_error',
] as const;
export type FunctionError = (typeof FUNCTION_ERRORS)[number];

/** Answer of `invite-member`. */
export const inviteResultSchema = z.object({
  personId: uuidSchema,
  accessId: uuidSchema,
  newAccount: z.boolean(),
  email: z.enum(['sent', 'not_configured', 'failed']),
});
export type InviteResult = z.infer<typeof inviteResultSchema>;

function parts(version: string): number[] | null {
  const match = /^(\d+)\.(\d+)\.(\d+)$/.exec(version.trim());
  return match === null ? null : match.slice(1).map(Number);
}

/**
 * Whether the app version is older than the minimum (`app_config.min_app_version`, PLAN §1.5).
 * Versions are `major.minor.patch`; anything else never blocks the app.
 */
export function isVersionOlder(version: string, minimum: string): boolean {
  const a = parts(version);
  const b = parts(minimum);
  if (a === null || b === null) {
    return false;
  }
  for (let i = 0; i < 3; i += 1) {
    const x = a[i] ?? 0;
    const y = b[i] ?? 0;
    if (x !== y) {
      return x < y;
    }
  }
  return false;
}
